import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Article 5 - 頂級食材解析
 * SEO 由 ArticlePage.jsx 集中管理，此組件只負責內容渲染
 */
export default function Article5() {
  const seafoodItems = [
    { name: '藍鰭鮪魚（本鮪）', season: '冬季（12-2月）', origin: '大間、戶井', feature: '油脂豐富，風味濃郁', price: 'NT$ 2,000-30,000/kg' },
    { name: '北海道海膽', season: '夏季（6-8月）', origin: '利尻、禮文島', feature: '甜度高，無苦澀味', price: 'NT$ 8,000-15,000/kg' },
    { name: '牡丹蝦', season: '冬季（11-3月）', origin: '北海道', feature: '肉質彈牙，甜味十足', price: 'NT$ 3,000-6,000/kg' },
    { name: '真鯛（櫻鯛）', season: '春季（3-5月）', origin: '明石、瀨戶內海', feature: '肉質緊實，清甜', price: 'NT$ 1,500-4,000/kg' },
    { name: '河豚（虎河豚）', season: '冬季（10-3月）', origin: '下關', feature: '口感Q彈，鮮味濃', price: 'NT$ 5,000-12,000/kg' },
    { name: '鮑魚（黑鮑）', season: '全年', origin: '岩手、三陸', feature: '肉質厚實，鮮甜', price: 'NT$ 4,000-10,000/kg' },
    { name: '星鰻（穴子）', season: '夏季（6-8月）', origin: '對馬、東京灣', feature: '肉質細嫩，油脂適中', price: 'NT$ 2,000-5,000/kg' },
    { name: '竹莢魚（鯵）', season: '夏季（5-8月）', origin: '關西、九州', feature: '肉質鮮美，適度油脂', price: 'NT$ 800-2,000/kg' },
  ];

  const otherItems = [
    { name: '和牛（A5 等級）', season: '全年', origin: '神戶、松阪、近江', feature: '霜降細緻，入口即化', price: 'NT$ 8,000-20,000/kg' },
    { name: '松茸', season: '秋季（9-10月）', origin: '長野、京都', feature: '香氣獨特，稀有珍貴', price: 'NT$ 10,000-30,000/kg' },
    { name: '白松露', season: '秋季（10-12月）', origin: '義大利 Alba', feature: '香氣極致，極為稀有', price: 'NT$ 150,000-300,000/kg' },
    { name: '魚子醬（Caviar）', season: '全年', origin: '俄羅斯、伊朗', feature: '鹹鮮爆漿，極致奢華', price: 'NT$ 50,000-200,000/kg' },
    { name: '竹筍（京筍）', season: '春季（3-5月）', origin: '京都', feature: '清甜脆嫩，無苦澀', price: 'NT$ 2,000-5,000/kg' },
    { name: '山葵（本山葵）', season: '全年', origin: '靜岡、長野', feature: '香氣清新，不嗆鼻', price: 'NT$ 3,000-8,000/kg' },
    { name: '昆布（利尻昆布）', season: '全年（乾貨）', origin: '北海道利尻島', feature: '鮮味濃郁，高級出汁', price: 'NT$ 5,000-15,000/kg' },
  ];

  return (
    <div>
      {/* 引言 */}
      <section className="mb-12">
        <p className="text-lg leading-relaxed text-gray-700 dark:text-gray-300 mb-6">
          在 Omakase 的世界中，「食材」是一切的根本。即使是最高超的廚師技藝，也無法彌補食材品質的不足。本文將帶您深入了解頂級食材的選擇標準、處理技巧，以及從產地到餐桌的完整旅程，揭開高級 Omakase 背後的食材秘密。
        </p>
      </section>

      {/* 築地/豐洲市場 */}
      <section className="mb-16">
        <h2 id="tsukiji-toyosu" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-purple-500">
          築地到豐洲：全球最大的魚市場
        </h2>
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-800 dark:to-gray-700 p-8 rounded-2xl mb-8">
          <h3 id="market-history" className="text-2xl font-bold text-gray-900 dark:text-white mb-4">市場歷史與轉變</h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            築地市場（Tsukiji Market）運營了 83 年，是全球最大的海鮮批發市場。2018 年 10 月，市場遷移至豐洲（Toyosu），但「築地」的名號和精神仍在全球 Omakase 界具有象徵性意義。
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: '築地時代（1935-2018）', colorClass: 'blue', items: ['每日交易量達 2,000 噸海鮮', '凌晨 5 點的鮪魚競標是重頭戲', '全球廚師的朝聖地', '傳統市場氛圍濃厚'] },
              { title: '豐洲時代（2018-現在）', colorClass: 'cyan', items: ['現代化設施，溫控更精準', '衛生標準更高', '空間更大，動線更流暢', '保留傳統競標文化'] },
            ].map(col => (
              <div key={col.title} className="bg-white dark:bg-gray-800 p-4 rounded-xl">
                <h4 className={`font-bold text-${col.colorClass}-600 dark:text-${col.colorClass}-400 mb-2`}>{col.title}</h4>
                <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">{col.items.map(i => <li key={i}>• {i}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 頂級食材列表 */}
      <section className="mb-16">
        <h2 id="premium-ingredients" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-purple-500">
          Omakase 中的頂級食材
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 id="seafood-premium" className="text-xl font-bold text-gray-900 dark:text-white mb-4">海鮮類</h3>
            {seafoodItems.map(item => (
              <div key={item.name} className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow">
                <h4 className="font-bold text-blue-600 dark:text-blue-400 mb-2">{item.name}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <div><strong>最佳季節：</strong>{item.season}</div>
                  <div><strong>主要產地：</strong>{item.origin}</div>
                  <div className="col-span-2"><strong>特色：</strong>{item.feature}</div>
                  <div className="col-span-2"><strong>價格：</strong>{item.price}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-4">
            <h3 id="other-premium" className="text-xl font-bold text-gray-900 dark:text-white mb-4">肉類與其他</h3>
            {otherItems.map(item => (
              <div key={item.name} className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow">
                <h4 className="font-bold text-green-600 dark:text-green-400 mb-2">{item.name}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <div><strong>最佳季節：</strong>{item.season}</div>
                  <div><strong>主要產地：</strong>{item.origin}</div>
                  <div className="col-span-2"><strong>特色：</strong>{item.feature}</div>
                  <div className="col-span-2"><strong>價格：</strong>{item.price}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 熟成技術 */}
      <section className="mb-16">
        <h2 id="aging-techniques" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-purple-500">
          食材熟成技術的科學
        </h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
          熟成（Aging）是提升食材風味的重要技術。透過控制溫度、濕度和時間，讓食材內部的酵素作用，分解蛋白質和脂肪，產生更豐富的鮮味和香氣。
        </p>
        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              id: 'fish-aging', icon: '🐟', title: '魚類熟成',
              techniques: [
                { name: '昆布締（Kobujime）', colorClass: 'blue', desc: '將魚肉夾在昆布之間，利用昆布的鮮味滲透魚肉，同時吸收多餘水分。', details: ['時間：2-24 小時', '溫度：0-5°C', '效果：增加鮮味，緊實口感', '適合：白身魚、比目魚、鯛魚'] },
                { name: '濕式熟成（Wet Aging）', colorClass: 'cyan', desc: '在真空包裝或冷藏狀態下，讓魚肉自然熟成。', details: ['時間：3-14 天', '溫度：-2 到 2°C', '效果：肉質變軟，風味濃郁', '適合：鮪魚、鰹魚、鰤魚'] },
              ],
            },
            {
              id: 'meat-aging', icon: '🥩', title: '肉類熟成',
              techniques: [
                { name: '乾式熟成（Dry Aging）', colorClass: 'red', desc: '在控溫控濕的環境中，讓肉品自然風乾熟成。', details: ['時間：21-60 天', '溫度：1-3°C', '濕度：75-85%', '效果：風味濃縮，產生堅果奶油香氣'] },
                { name: '濕式熟成（Wet Aging）', colorClass: 'orange', desc: '在真空包裝中進行熟成，保留肉汁。', details: ['時間：7-28 天', '溫度：0-2°C', '效果：保留更多肉汁，肉質變軟', '成本較低'] },
              ],
            },
          ].map(section => (
            <div key={section.id} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
              <h3 id={section.id} className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <span className="text-3xl">{section.icon}</span>{section.title}
              </h3>
              <div className="space-y-4">
                {section.techniques.map(tech => (
                  <div key={tech.name} className={`bg-${tech.colorClass}-50 dark:bg-${tech.colorClass}-900/20 p-4 rounded-xl`}>
                    <h4 className={`font-bold text-${tech.colorClass}-600 dark:text-${tech.colorClass}-400 mb-2`}>{tech.name}</h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{tech.desc}</p>
                    <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                      {tech.details.map(d => <li key={d}>• {d}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 結語 */}
      <section className="mb-16">
        <h2 id="conclusion" className="text-3xl font-bold text-gray-900 dark:text-white mb-6">結語</h2>
        <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 p-8 rounded-2xl">
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            頂級食材是 Omakase 的靈魂，但更重要的是對食材的理解、尊重和善用。從豐洲市場凌晨的競標，到餐桌上的每一道料理，背後都是無數人的專業、經驗和熱情。
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
            下次享用 Omakase 時，不妨多詢問食材的故事，與廚師交流。這不僅是對食材和廚師的尊重，更能讓您的用餐體驗更加豐富和有意義。
          </p>
        </div>
      </section>

      {/* 延伸閱讀 */}
      <section className="mt-16 pt-8 border-t-2 border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">延伸閱讀</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { to: '/beginners-complete-guide-to-omakase', icon: '📚', title: '初學者的 Omakase 完整指南', desc: '從零開始了解 Omakase 的基礎知識', gradient: 'from-orange-400 to-rose-500', hover: 'orange' },
            { to: '/spring-omakase-sakura-season', icon: '🌸', title: '春季 Omakase 特色', desc: '探索春季料理的獨特魅力', gradient: 'from-pink-400 to-rose-500', hover: 'pink' },
            { to: '/taiwan-omakase-culture-development', icon: '🇹🇼', title: '台灣 Omakase 文化', desc: '了解台灣 Omakase 的發展', gradient: 'from-green-400 to-emerald-500', hover: 'green' },
          ].map(link => (
            <Link key={link.to} to={link.to} className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className={`h-48 bg-gradient-to-br ${link.gradient} flex items-center justify-center`}>
                <span className="text-6xl">{link.icon}</span>
              </div>
              <div className="p-6">
                <h3 className={`text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-${link.hover}-500 transition-colors`}>{link.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{link.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}